import { useMemo, useCallback } from 'react';
import { useQuery } from 'react-query';
import useAuth from '../../../hooks/useAuth';
import { getEntities } from '../../request/assets';
import { IUserAccount } from '../../../libs/api-client';
import { isSameOrHigherRole } from '../role';
import { ROLE, ROUTE } from '../../../types';

export function useImpersonation() {
  const { isImpersonating, isRoleSupportRoOrAbove, loggedIn, realUser } = useAuth();

  const canImpersonateUser = useCallback(
    (user: IUserAccount) => {
      if (!realUser) {
        return false;
      }

      const hasSameOrHigherRole = !user.roles?.length
        ? true
        : user.roles.some(userRole => isSameOrHigherRole(userRole, realUser.role));

      return (
        !hasSameOrHigherRole &&
        user.status === 'ACTIVE' &&
        user.active &&
        user.email !== realUser.email
      );
    },
    [realUser]
  );

  const enableImpersonation: boolean = useMemo(
    () => loggedIn && !!realUser && isRoleSupportRoOrAbove(realUser.role as ROLE),
    [loggedIn, realUser, isRoleSupportRoOrAbove]
  );

  const { data: users } = useQuery(
    [ROUTE.userAccount.queryKey, 'impersonation-users', realUser?.id],
    () =>
      getEntities<IUserAccount>(
        `${ROUTE.userAccount.api}?pagination=false&status=ACTIVE&active=true`
      ),
    { enabled: !isImpersonating && enableImpersonation }
  );

  const impersonationUsers: IUserAccount[] | null = useMemo(
    () => (!users || !enableImpersonation ? null : users?.filter(user => canImpersonateUser(user))),
    [canImpersonateUser, enableImpersonation, users]
  );

  return {
    canImpersonateUser,
    enableImpersonation,
    impersonationUsers,
  };
}
